"use client";

import * as React from "react";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/app/lib/utils";

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function safeParseInt(v: string, fallback: number) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
}

export interface NumberStepperProps {
  ariaLabel: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
}

/**
 * Number input with increment/decrement buttons (same as Pomodoro timer).
 */
export function NumberStepper({
  ariaLabel,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  className,
  inputClassName,
}: NumberStepperProps) {
  const stepDown = () => onChange(clamp(value - step, min, max));
  const stepUp = () => onChange(clamp(value + step, min, max));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = safeParseInt(e.target.value, value);
    onChange(clamp(parsed, min, max));
  };

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-lg border px-2",
        className
      )}
    >
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        disabled={disabled || value <= min}
        onClick={stepDown}
        aria-label={`decrease ${ariaLabel}`}
      >
        <ChevronDown className="h-3 w-3" />
      </Button>
      <Input
        aria-label={ariaLabel}
        type="number"
        inputMode="numeric"
        className={cn(
          "w-12 border-none focus-visible:ring-0 text-center p-0 h-8",
          inputClassName
        )}
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={handleInputChange}
      />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        disabled={disabled || value >= max}
        onClick={stepUp}
        aria-label={`increase ${ariaLabel}`}
      >
        <ChevronUp className="h-3 w-3" />
      </Button>
    </div>
  );
}
